/**
 * Appointments Page - List and manage appointments
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointment } from '@/contexts/AppointmentContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';
import { Calendar, Clock, User, MapPin, Plus, Filter } from 'lucide-react';
import { toast } from '@/lib/toast';
import { Appointment } from '@/types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getBarberByUserId } from '@/services/barberService';

export default function AppointmentsPage() {
  const navigate = useNavigate();
  const { appointments, loading, listAppointments, cancelAppointment, confirmAppointment, completeAppointment } = useAppointment();
  const { user, firebaseUser } = useAuth();
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'completed'>('all');
  const [barberId, setBarberId] = useState<string | null>(null);
  const [loadingBarberId, setLoadingBarberId] = useState(false);

  // Get the correct barberId from the barbers collection using userId (for barbers only)
  useEffect(() => {
    async function fetchBarberId() {
      if (user?.role !== 'barber' || !firebaseUser?.uid) {
        setBarberId(null);
        setLoadingBarberId(false);
        return;
      }

      try {
        setLoadingBarberId(true);
        const barber = await getBarberByUserId(firebaseUser.uid);
        if (barber) {
          setBarberId(barber.barberId);
          console.log('✅ Barber found for appointments:', {
            userId: firebaseUser.uid,
            barberId: barber.barberId,
            displayName: barber.displayName
          });
        } else {
          console.warn('⚠️ No barber document found for userId:', firebaseUser.uid);
          setBarberId(null);
        }
      } catch (error) {
        console.error('❌ Error fetching barber:', error);
        setBarberId(null);
      } finally {
        setLoadingBarberId(false);
      }
    }

    fetchBarberId();
  }, [user?.role, firebaseUser?.uid]);

  // Load appointments when filter changes or barberId is fetched
  useEffect(() => {
    // Wait for barberId to be fetched if user is a barber
    if (user?.role === 'barber' && loadingBarberId) {
      return;
    }
    loadAppointments();
  }, [filter, barberId, loadingBarberId]);

  const loadAppointments = async () => {
    try {
      const filters: any = {};

      // Filter by user role
      if (user?.role === 'barber') {
        // Use the correct barberId from barbers collection, not user.id
        if (barberId) {
          filters.barberId = barberId;
        } else {
          // No barber document found, return empty
          console.warn('No barberId found for barber user');
          return;
        }
      } else if (user?.role === 'client') {
        filters.userId = user.id;
      }

      // Filter by status
      if (filter !== 'all') {
        filters.status = filter;
      }

      await listAppointments(filters);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error('Error al cargar las citas');
    }
  };

  const handleCancel = async (appointmentId: string) => {
    try {
      await cancelAppointment(appointmentId);
      toast.success('Cita cancelada exitosamente');
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Error al cancelar la cita');
    }
  };

  const handleConfirm = async (appointmentId: string) => {
    try {
      await confirmAppointment(appointmentId);
      toast.success('Cita confirmada exitosamente');
    } catch (error) {
      console.error('Error confirming appointment:', error);
      toast.error('Error al confirmar la cita');
    }
  };

  const handleComplete = async (appointmentId: string) => {
    try {
      await completeAppointment(appointmentId);
      toast.success('Cita completada exitosamente');
    } catch (error) {
      console.error('Error completing appointment:', error);
      toast.error('Error al completar la cita');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-accent/10 text-accent';
      case 'confirmed':
        return 'bg-info/10 text-info';
      case 'in_progress':
        return 'bg-accent/10 text-accent';
      case 'completed':
        return 'bg-success/10 text-success';
      case 'cancelled':
        return 'bg-danger/10 text-danger';
      case 'no_show':
        return 'bg-surface-2 text-ink bg-surface-2 text-ink-muted';
      default:
        return 'bg-surface-2 text-ink bg-surface-2 text-ink-muted';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'confirmed':
        return 'Confirmada';
      case 'in_progress':
        return 'En progreso';
      case 'completed':
        return 'Completada';
      case 'cancelled':
        return 'Cancelada';
      case 'no_show':
        return 'No asistió';
      default:
        return status;
    }
  };

  const filteredAppointments = appointments || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-ink">Citas</h1>
          <p className="mt-2 text-ink-muted">
            Gestiona tus citas programadas
          </p>
        </div>

        <Button
          onClick={() => navigate('/appointments/book')}
          className="flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nueva Cita
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-lg shadow-sm p-4 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-5 h-5 text-ink-subtle" />
          <span className="text-sm font-medium text-ink-muted">Filtrar:</span>

          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-info text-white'
                  : 'bg-surface-2 text-ink-muted hover:bg-surface-2 text-ink-muted hover:bg-surface-3'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'pending'
                  ? 'bg-info text-white'
                  : 'bg-surface-2 text-ink-muted hover:bg-surface-2 text-ink-muted hover:bg-surface-3'
              }`}
            >
              Pendientes
            </button>
            <button
              onClick={() => setFilter('confirmed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'confirmed'
                  ? 'bg-info text-white'
                  : 'bg-surface-2 text-ink-muted hover:bg-surface-2 text-ink-muted hover:bg-surface-3'
              }`}
            >
              Confirmadas
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'completed'
                  ? 'bg-info text-white'
                  : 'bg-surface-2 text-ink-muted hover:bg-surface-2 text-ink-muted hover:bg-surface-3'
              }`}
            >
              Completadas
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {(loading || loadingBarberId) && (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-info/30 border-r-transparent"></div>
          <p className="mt-4 text-ink-muted">Cargando citas...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !loadingBarberId && filteredAppointments.length === 0 && (
        <div className="text-center py-12 bg-surface rounded-lg shadow-sm">
          <Calendar className="mx-auto h-12 w-12 text-ink-muted" />
          <h3 className="mt-4 text-lg font-medium text-ink">
            No hay citas
          </h3>
          <p className="mt-2 text-ink-muted">
            {filter === 'all' ? 'No tienes citas programadas' : `No hay citas ${getStatusText(filter).toLowerCase()}`}
          </p>
          <Button
            onClick={() => navigate('/appointments/book')}
            className="mt-6"
          >
            Agendar primera cita
          </Button>
        </div>
      )}

      {/* Appointments List */}
      {!loading && !loadingBarberId && filteredAppointments.length > 0 && (
        <div className="grid gap-4">
          {filteredAppointments.map((appointment: Appointment) => (
            <div
              key={appointment.appointmentId}
              className="bg-surface rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Appointment Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-ink">
                        {appointment.serviceName}
                      </h3>
                      <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(appointment.status)}`}>
                        {getStatusText(appointment.status)}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-ink">
                      €{(appointment.servicePrice / 100).toFixed(2)}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-base">
                    <div className="flex items-center gap-2 text-ink-muted">
                      <Calendar className="w-4 h-4" />
                      <span>{format(appointment.dateTime.toDate(), "EEEE, d 'de' MMMM yyyy", { locale: es })}</span>
                    </div>
                    <div className="flex items-center gap-2 text-ink-muted">
                      <Clock className="w-4 h-4" />
                      <span>{format(appointment.dateTime.toDate(), 'HH:mm')} ({appointment.serviceDuration} min)</span>
                    </div>
                    <div className="flex items-center gap-2 text-ink-muted">
                      <User className="w-4 h-4" />
                      <span>{user?.role === 'barber' ? appointment.userName : appointment.barberName}</span>
                    </div>
                    {appointment.notes && (
                      <div className="flex items-start gap-2 text-ink-muted col-span-2">
                        <MapPin className="w-4 h-4 mt-0.5" />
                        <span className="text-sm">{appointment.notes}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {appointment.status === 'pending' && user?.role === 'barber' && (
                    <Button
                      onClick={() => handleConfirm(appointment.appointmentId)}
                      variant="outline"
                      size="sm"
                    >
                      Confirmar
                    </Button>
                  )}

                  {appointment.status === 'confirmed' && user?.role === 'barber' && (
                    <Button
                      onClick={() => handleComplete(appointment.appointmentId)}
                      variant="outline"
                      size="sm"
                    >
                      Completar
                    </Button>
                  )}

                  {(appointment.status === 'pending' || appointment.status === 'confirmed') && (
                    <Button
                      onClick={() => handleCancel(appointment.appointmentId)}
                      variant="outline"
                      size="md"
                      className="text-danger hover:text-danger"
                    >
                      Cancelar
                    </Button>
                  )}

                  <Button
                    onClick={() => navigate(`/appointments/${appointment.appointmentId}`)}
                    variant="outline"
                    size="sm"
                  >
                    Ver detalles
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
